import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, CircularProgress, Alert, Chip,
  Tabs, Tab, Card, CardContent, Divider, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  List, ListItem, ListItemIcon, ListItemText, IconButton,
  Modal, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, RadioGroup, Radio,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
} from '@mui/material';
import {
  ArrowBack, Edit, Delete, Assignment, Business, Person,
  AttachMoney, CalendarToday, Info, AccountTree, Receipt,
  Assessment, Archive, Settings, Folder, PictureAsPdf,
  InsertDriveFile, Download, Visibility, Upload, Save, Cancel,
  RequestPage, Warning, CheckCircle, Search, Description,
  AttachFile, MoneyOff, Gavel, Add, Close, VideoCall,
} from '@mui/icons-material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { expedientesApi, clientesApi, tecnicosApi, documentosApi, API_BASE_URL } from '../services/api';
import { Cliente } from '../types/expediente';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`expediente-tabpanel-${index}`}
      aria-labelledby={`expediente-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Componente para mostrar código con información adicional
const FormatCodigoWithInfo: React.FC<{ codigo: string }> = ({ codigo }) => {
  const [expedienteInfo, setExpedienteInfo] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const buscarInfoExpediente = async () => {
      try {
        setLoading(true);
        const expedientes = await expedientesApi.getExpedientes(0, 10000);
        const exp = expedientes.find((e: any) => e.codigo_actual === codigo);
        
        if (exp) {
          const info = `${codigo} --- ${exp.tipo || 'SIN'}-${exp.numero || '?'}-${exp.anio || '?'}`;
          setExpedienteInfo(info);
        } else {
          setExpedienteInfo(`${codigo} --- (No encontrado)`);
        }
      } catch (error) {
        setExpedienteInfo(`${codigo} --- (Error cargando)`);
      } finally {
        setLoading(false);
      }
    };
    
    if (codigo) {
      buscarInfoExpediente();
    }
  }, [codigo]);

  if (loading) {
    return <span>{codigo} --- <CircularProgress size={12} /></span>;
  }
  
  return <span>{expedienteInfo}</span>;
};

const ExpedienteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Estados principales
  const [expediente, setExpediente] = useState<any>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [tecnicos, setTecnicos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingTecnicos, setLoadingTecnicos] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Estados para minutas
  const [minutaDialog, setMinutaDialog] = useState(false);
  const [minutaForm, setMinutaForm] = useState({ juzgado: '', ciudad: '', fecha_presentacion_minuta: '', notas: '' });
  const [enviandoMinuta, setEnviandoMinuta] = useState(false);
  const [minutaSnack, setMinutaSnack] = useState('');

  // ── Estados para el Dialog de Dictamen Pericial ─────────────────────────
  const [dictamenDialog, setDictamenDialog]     = useState(false);
  const [generandoDictamen, setGenerandoDictamen] = useState(false);
  const [dictamenSnack, setDictamenSnack]       = useState('');
  const [dictamenForm, setDictamenForm] = useState<{
    fecha_documento: string;
    tipo_procedimiento_completo: string;
    // Sección 01
    objeto_examen_tipo: '' | 'desperfectos' | 'sustraidos' | 'personalizado';
    descripcion_objeto_examen: string;
    // Sección 02
    extremos_propuestos: string[];
    extremo_nuevo: string;
    // Sección 03
    visita_realizada: boolean;
    visita_fecha: string;
    hablo_demandante: boolean | null;
    demandante_fecha: string;
    nota_adicional: string;           // texto final editable
    // Sección 04
    partidas: { descripcion: string; importe: string }[];
    partida_nueva_desc: string;
    partida_nueva_importe: string;
    obs_aportaron_docs: boolean;      // opción: se aportaron valoraciones
    obs_obras: boolean;               // opción: importes relativos a obras
    obs_no_contrastados: boolean;     // opción: daños no contrastados
    obs_generico: boolean;            // opción: texto genérico
    observaciones_valoracion: string; // texto final editable
    // Sección 04.02 – Desglose MO / Materiales
    mano_obra_sin_iva: string;
    materiales_sin_iva: string;
    desglose_origen: 'mano_obra' | 'materiales' | '';
  }>({
    fecha_documento: new Date().toISOString().slice(0, 10),
    tipo_procedimiento_completo: '',
    objeto_examen_tipo: '',
    descripcion_objeto_examen: '',
    extremos_propuestos: [],
    extremo_nuevo: '',
    visita_realizada: false,
    visita_fecha: '',
    hablo_demandante: null,
    demandante_fecha: '',
    nota_adicional: '',
    partidas: [],
    partida_nueva_desc: '',
    partida_nueva_importe: '',
    obs_aportaron_docs: false,
    obs_obras: false,
    obs_no_contrastados: false,
    obs_generico: false,
    observaciones_valoracion: '',
    mano_obra_sin_iva: '',
    materiales_sin_iva: '',
    desglose_origen: '',
  });

  // Estados para edición
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState<any>(null);
  const [formData, setFormData] = useState<any>({
    nig: '',
    codigo_actual: '',
    codigo_original: '',
    tipo: '',
    numero: null,
    anio: null,
    estado: '',
    justicia_gratuita: false,
    fecha_inicio: '',
    fecha_encargo: '',
    fecha_informe: '',
    importe: null,
    importe_iva: null,
    cobrado: false,
    observaciones_generales: '',
    nivel: null,
    proviene_de: '',
    se_ha_convertido_en: '',
    cliente_id: '',
    tecnico_id: ''
  });


  // Estados para búsqueda de expedientes
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [currentSearchField, setCurrentSearchField] = useState<'proviene_de' | 'se_ha_convertido_en' | null>(null);
  const [searchNumber, setSearchNumber] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Estados para confirmación de cambios
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingExpediente, setPendingExpediente] = useState<any>(null);
  const [pendingField, setPendingField] = useState<string>('');
  const [pendingChanges, setPendingChanges] = useState<string>('');

  // Estados para crear un expediente nuevo como "se ha convertido en"
  const [crearConvertidoModalOpen, setCrearConvertidoModalOpen] = useState(false);
  const [crearConvertidoForm, setCrearConvertidoForm] = useState({
    codigo_actual: '',
    codigo_original: '',
    tipo: '',
    numero: undefined as number | undefined,
    anio: new Date().getFullYear(),
    nig: '',
    cliente_id: '',
    tecnico_id: '',
    justicia_gratuita: false,
    observaciones_generales: '',
  });
  const [creandoConvertido, setCreandoConvertido] = useState(false);
  const [errorCrearConvertido, setErrorCrearConvertido] = useState<string | null>(null);
  const [loadingCodigoConvertido, setLoadingCodigoConvertido] = useState(false);

  // Estados para documentos
  const [documentos, setDocumentos] = useState<any[]>([]);
  const [documentTab, setDocumentTab] = useState(0);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [errorDocumentos, setErrorDocumentos] = useState<string | null>(null);

  // Estados para eliminación
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentoToDelete, setDocumentoToDelete] = useState<any>(null);
  const [deletingDocumento, setDeletingDocumento] = useState<string | null>(null);

  // Estados para eliminación de expediente
  const [deleteExpedienteConfirmOpen, setDeleteExpedienteConfirmOpen] = useState(false);
  const [deletingExpediente, setDeletingExpediente] = useState(false);
  const [errorEliminarExpediente, setErrorEliminarExpediente] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadExpediente(id);
    }
  }, [id]);

  // CARGAR CLIENTES Y TÉCNICOS AL INICIALIZAR EL COMPONENTE
  useEffect(() => {
    loadClientes();
    loadTecnicos();
  }, []);

  // Cargar documentos cuando cambie el expediente
  useEffect(() => {
    if (expediente?.id) {
      loadDocumentos();
    }
  }, [expediente?.id]);

  useEffect(() => {
    if (expediente) {
      setOriginalData(expediente);
      setFormData({
        nig: expediente.nig || '',
        codigo_actual: expediente.codigo_actual || '',
        codigo_original: expediente.codigo_original || '',
        tipo: expediente.tipo || '',
        numero: expediente.numero || null,
        anio: expediente.anio || null,
        estado: expediente.estado || '',
        justicia_gratuita: expediente.justicia_gratuita || false,
        fecha_inicio: expediente.fecha_inicio || '',
        fecha_encargo: expediente.fecha_encargo || '',
        fecha_informe: expediente.fecha_informe || '',
        importe: expediente.importe || null,
        importe_iva: expediente.importe_iva || null,
        cobrado: expediente.cobrado || false,
        observaciones_generales: expediente.observaciones_generales || '',
        nivel: expediente.nivel || null,
        proviene_de: expediente.proviene_de || '',
        se_ha_convertido_en: expediente.se_ha_convertido_en || '',
        cliente_id: expediente.cliente_id || '',
        tecnico_id: expediente.tecnico_id || '',
        es_activo: expediente.es_activo || false
      });
    }
  }, [expediente]);

  // Detectar cambios en el formulario
  useEffect(() => {
    if (originalData && editMode && expediente) {
      const hasChangesNow = JSON.stringify(formData) !== JSON.stringify({
        nig: originalData.nig || '',
        codigo_actual: originalData.codigo_actual || '',
        codigo_original: originalData.codigo_original || '',
        tipo: originalData.tipo || '',
        numero: originalData.numero || null,
        anio: originalData.anio || null,
        estado: originalData.estado || '',
        justicia_gratuita: originalData.justicia_gratuita || false,
        fecha_inicio: originalData.fecha_inicio || '',
        fecha_encargo: originalData.fecha_encargo || '',
        fecha_informe: originalData.fecha_informe || '',
        importe: originalData.importe || null,
        importe_iva: originalData.importe_iva || null,
        cobrado: originalData.cobrado || false,
        observaciones_generales: originalData.observaciones_generales || '',
        nivel: originalData.nivel || null,
        proviene_de: originalData.proviene_de || '',
        se_ha_convertido_en: originalData.se_ha_convertido_en || '',
        cliente_id: originalData.cliente_id || '',
        tecnico_id: originalData.tecnico_id || ''
      });
      setHasChanges(hasChangesNow);
    }
  }, [formData, originalData, editMode, expediente]);

  // Advertir antes de salir con cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    if (hasChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [hasChanges]);

  const loadExpediente = async (expedienteId: string) => {
    try {
      setLoading(true);
      const data = await expedientesApi.getExpediente(expedienteId);
      setExpediente(data);
    } catch (err) {
      setError('Error cargando expediente');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar clientes para el desplegable
  const loadClientes = async () => {
    try {
      setLoadingClientes(true);
      const data = await clientesApi.getClientes(0, 1000);
      setClientes(data);
    } catch (err) {
      console.error('Error cargando clientes:', err);
      setError('Error cargando lista de clientes');
    } finally {
      setLoadingClientes(false);
    }
  };

  // Cargar técnicos para el desplegable
  const loadTecnicos = async () => {
    try {
      setLoadingTecnicos(true);
      const data = await tecnicosApi.getTecnicos(0, 100, true);
      setTecnicos(data);
    } catch (err) {
      console.error('Error cargando técnicos:', err);
    } finally {
      setLoadingTecnicos(false);
    }
  };

  // Función para obtener nombre del cliente
  const getClienteNombre = (clienteId: string | null | undefined) => {
    if (!clienteId) return '-';
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? cliente.nombre : `ID: ${clienteId}`;
  };

  // Función para obtener nombre del técnico
  const getTecnicoNombre = (tecnicoId: string | null | undefined) => {
    if (!tecnicoId) return '-';
    const tecnico = tecnicos.find(t => t.id === tecnicoId);
    if (tecnico) {
      const nombreCompleto = `${tecnico.nombre} ${tecnico.apellidos || ''}`.trim();
      return nombreCompleto || 'Técnico sin nombre';
    }
    return `ID: ${tecnicoId}`;
  };

  // Función para abrir modal de búsqueda
  const handleOpenSearchModal = (field: 'proviene_de' | 'se_ha_convertido_en') => {
    setCurrentSearchField(field);
    setSearchNumber('');
    setSearchResults([]);
    setSearchError(null);
    setSearchModalOpen(true);
  };

  // Función para buscar expedientes por número
  const handleSearchExpedientes = async () => {
    if (!searchNumber.trim()) {
      setSearchError('Por favor, introduce un número de expediente');
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError(null);
      
      // Obtener todos los expedientes y filtrar por número exacto
      const expedientes = await expedientesApi.getExpedientes(0, 10000);
      
      const filtered = expedientes.filter((exp: any) => 
        exp.numero?.toString() === searchNumber.trim()
      );

      if (filtered.length === 0) {
        setSearchError(`No se encontraron expedientes con el número ${searchNumber}`);
        setSearchResults([]);
      } else {
        setSearchResults(filtered);
        setSearchError(null);
      }
    } catch (err: any) {
      setSearchError('Error al buscar expedientes: ' + (err.message || 'Error desconocido'));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Función para seleccionar expediente de los resultados - VERSIÓN MEJORADA
  const handleSelectExpediente = (expedienteSeleccionado: any) => {
    if (!currentSearchField || !expedienteSeleccionado.codigo_actual) {
      return;
    }

    // Preparar información para el modal de confirmación
    const codigoActualDelExpedienteActual = expediente.codigo_actual;
    
    let cambiosResumen = `
🔄 CAMBIOS QUE SE REALIZARÁN:

📋 EXPEDIENTE ACTUAL (${codigoActualDelExpedienteActual}):
• Campo "${currentSearchField}": ${expedienteSeleccionado.codigo_actual}
• Código original: ${expedienteSeleccionado.codigo_original || 'Sin código original'} → se copiará aquí

🔄 EXPEDIENTE SELECCIONADO (${expedienteSeleccionado.codigo_actual}):
• Estado: ${expedienteSeleccionado.estado} → "Convertido"
• Activo: ${expedienteSeleccionado.es_activo ? 'Sí' : 'No'} → No
`;

    // Lógica específica según el campo
    if (currentSearchField === 'proviene_de') {
      cambiosResumen += `• Se ha convertido en: ${expedienteSeleccionado.se_ha_convertido_en || 'Vacío'} → ${codigoActualDelExpedienteActual}`;
    }

    cambiosResumen += `

¿Confirmas estos cambios?`;

    // Guardar datos para procesamiento posterior
    setPendingExpediente(expedienteSeleccionado);
    setPendingField(currentSearchField);
    setPendingChanges(cambiosResumen);
    setConfirmModalOpen(true);
  };

  // Función para confirmar y procesar todos los cambios
  const handleConfirmChanges = async () => {
    if (!pendingExpediente || !pendingField) {
      return;
    }

    try {
      setConfirmModalOpen(false);
      setSaving(true);

      console.log('🔄 Vinculando cadena procesal...');
      console.log('📊 ID del expediente:', expediente.id, '| Campo:', pendingField, '| Relacionado:', pendingExpediente.codigo_actual);

      // Operación controlada de cadena: enlaza predecesor/sucesor, recalcula cadena_id
      // vía trigger de BD y traslada la minuta si corresponde (ver vincular-cadena en backend)
      const expedienteActualizado = await expedientesApi.vincularCadena(
        expediente.id,
        pendingField as 'proviene_de' | 'se_ha_convertido_en',
        pendingExpediente.codigo_actual
      );

      // ACTUALIZAR ESTADOS LOCALES
      setExpediente(expedienteActualizado);
      setOriginalData(expedienteActualizado);
      
      // ACTUALIZAR formData para reflejar los cambios
      const nuevoFormData = {
        ...formData,
        [pendingField]: pendingExpediente.codigo_actual,
        codigo_original: pendingExpediente.codigo_original || null
      };
      setFormData(nuevoFormData);
      
      // 4. LIMPIAR ESTADOS Y CERRAR MODALES
      setSearchModalOpen(false);
      setSearchNumber('');
      setSearchResults([]);
      setCurrentSearchField(null);
      setPendingExpediente(null);
      setPendingField('');
      setPendingChanges('');
      setHasChanges(false);

      console.log('✅ Todos los cambios realizados correctamente');

    } catch (err: any) {
      console.error('❌ Error procesando cambios completo:', err);
      
      // MANEJO DE ERRORES MEJORADO
      if (err.response?.status === 422) {
        console.error('📋 Error 422 - Detalles:', err.response.data);

        if (err.response.data?.detail && Array.isArray(err.response.data.detail)) {
          const erroresValidacion = err.response.data.detail.map((validationError: any) =>
            `${validationError.loc?.join('.')}: ${validationError.msg}`
          ).join(', ');
          setError('Errores de validación: ' + erroresValidacion);
        } else {
          setError('Error de validación: ' + (err.response.data?.detail || 'Error desconocido'));
        }
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Error al procesar los cambios: ' + (err.message || 'Error desconocido'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Función para cancelar los cambios pendientes
  const handleCancelChanges = () => {
    setConfirmModalOpen(false);
    setPendingExpediente(null);
    setPendingField('');
    setPendingChanges('');
  };

  // Función para cerrar modal de búsqueda
  const handleCloseSearchModal = () => {
    setSearchModalOpen(false);
    setSearchNumber('');
    setSearchResults([]);
    setSearchError(null);
    setCurrentSearchField(null);
  };

  // ==================== CREAR EXPEDIENTE "SE HA CONVERTIDO EN" ====================

  // Siguiente código_actual libre, mismo criterio que el alta normal (Dashboard)
  const obtenerSiguienteCodigoActual = async (): Promise<string> => {
    const expedientes = await expedientesApi.getExpedientes(0, 10000);
    const codigosExistentes = expedientes
      .map((exp: any) => exp.codigo_actual)
      .filter((codigo: any) => codigo && !isNaN(Number(codigo)))
      .map((codigo: any) => Number(codigo))
      .sort((a: number, b: number) => a - b);

    let siguienteCodigo = 1;
    for (const codigo of codigosExistentes) {
      if (codigo === siguienteCodigo) {
        siguienteCodigo++;
      } else {
        break;
      }
    }
    return siguienteCodigo.toString();
  };

  const handleOpenCrearConvertidoModal = async () => {
    setErrorCrearConvertido(null);
    setCrearConvertidoForm({
      codigo_actual: '',
      codigo_original: '',
      tipo: expediente?.tipo || '',
      numero: undefined,
      anio: new Date().getFullYear(),
      nig: expediente?.nig || '',
      cliente_id: expediente?.cliente_id || '',
      tecnico_id: expediente?.tecnico_id || '',
      justicia_gratuita: expediente?.justicia_gratuita || false,
      observaciones_generales: '',
    });
    setCrearConvertidoModalOpen(true);

    try {
      setLoadingCodigoConvertido(true);
      const siguienteCodigo = await obtenerSiguienteCodigoActual();
      setCrearConvertidoForm(prev => ({
        ...prev,
        codigo_actual: siguienteCodigo,
        codigo_original: siguienteCodigo,
      }));
    } catch (err) {
      console.error('Error generando código automático:', err);
    } finally {
      setLoadingCodigoConvertido(false);
    }
  };

  const handleCloseCrearConvertidoModal = () => {
    if (creandoConvertido) return;
    setCrearConvertidoModalOpen(false);
    setErrorCrearConvertido(null);
  };

  const handleCrearConvertidoInputChange = (field: string, value: any) => {
    setCrearConvertidoForm(prev => ({ ...prev, [field]: value }));
  };

  const isCrearConvertidoFormValid = () => {
    return !!(
      crearConvertidoForm.tipo &&
      crearConvertidoForm.numero &&
      crearConvertidoForm.anio &&
      crearConvertidoForm.codigo_actual
    );
  };

  const handleConfirmarCrearConvertido = async () => {
    if (!expediente || !isCrearConvertidoFormValid()) return;

    try {
      setCreandoConvertido(true);
      setErrorCrearConvertido(null);

      const expedienteActualizado = await expedientesApi.crearYVincularCadena(expediente.id, {
        codigo_actual: crearConvertidoForm.codigo_actual || undefined,
        codigo_original: crearConvertidoForm.codigo_original || undefined,
        tipo: crearConvertidoForm.tipo,
        numero: crearConvertidoForm.numero as number,
        anio: crearConvertidoForm.anio,
        nig: crearConvertidoForm.nig || undefined,
        cliente_id: crearConvertidoForm.cliente_id || undefined,
        tecnico_id: crearConvertidoForm.tecnico_id || undefined,
        justicia_gratuita: crearConvertidoForm.justicia_gratuita,
        observaciones_generales: crearConvertidoForm.observaciones_generales || undefined,
      });

      setExpediente(expedienteActualizado);
      setOriginalData(expedienteActualizado);
      setFormData((prev: any) => ({
        ...prev,
        se_ha_convertido_en: expedienteActualizado.se_ha_convertido_en,
      }));
      setHasChanges(false);
      setCrearConvertidoModalOpen(false);
    } catch (err: any) {
      setErrorCrearConvertido(
        err.response?.data?.detail || err.message || 'Error creando y vinculando el nuevo expediente'
      );
    } finally {
      setCreandoConvertido(false);
    }
  };

  // ==================== FUNCIONES PARA DOCUMENTOS ====================
  
  // Cargar documentos del expediente
  const loadDocumentos = async () => {
    if (!expediente?.id) return;
    
    try {
      setLoadingDocumentos(true);
      setErrorDocumentos(null);
      
      const documentosData = await documentosApi.getDocumentosByExpediente(expediente.id);
      setDocumentos(documentosData);
      
    } catch (err: any) {
      setErrorDocumentos('Error cargando documentos: ' + (err.message || 'Error desconocido'));
      console.error('Error cargando documentos:', err);
    } finally {
      setLoadingDocumentos(false);
    }
  };

  // Confirmar eliminación
  const handleConfirmDelete = (documento: any) => {
    setDocumentoToDelete(documento);
    setDeleteConfirmOpen(true);
  };

  // Eliminar documento
  const handleDeleteDocumento = async () => {
    if (!documentoToDelete) return;

    try {
      setDeletingDocumento(documentoToDelete.id);

      const result = await documentosApi.deleteDocumento(documentoToDelete.id);

      // Mostrar mensaje de éxito
      console.log('Documento eliminado:', result.message);

      // Cerrar modal primero
      setDeleteConfirmOpen(false);

      // Esperar un momento antes de recargar para evitar conflictos con el cierre del modal
      setTimeout(async () => {
        // Recargar lista de documentos
        await loadDocumentos();

        // Limpiar estado
        setDocumentoToDelete(null);
        setDeletingDocumento(null);
      }, 100);

    } catch (err: any) {
      setErrorDocumentos('Error eliminando documento: ' + (err.message || 'Error desconocido'));
      console.error('Error eliminando documento:', err);
      setDeletingDocumento(null);
    }
  };

  // Eliminar expediente
  const handleDeleteExpediente = async () => {
    if (!expediente) return;

    try {
      setDeletingExpediente(true);
      setErrorEliminarExpediente(null);

      await expedientesApi.deleteExpediente(expediente.id);

      setDeleteExpedienteConfirmOpen(false);
      navigate('/expedientes');

    } catch (err: any) {
      setErrorEliminarExpediente(
        err.response?.data?.detail || err.message || 'Error desconocido al eliminar el expediente'
      );
      console.error('Error eliminando expediente:', err);
      setDeletingExpediente(false);
    }
  };

  const handleDownloadDocumento = async (documento: any) => {
    try {
      const blob = await documentosApi.downloadDocumento(documento.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = documento.nombre_archivo || 'documento';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      setErrorDocumentos('Error descargando documento: ' + (err.message || 'Error desconocido'));
    }
  };

  const handlePreviewDocumento = async (documento: any) => {
    const previewWindow = window.open('about:blank', '_blank');
    if (!previewWindow) {
      setErrorDocumentos('El navegador bloqueó la ventana de previsualización');
      return;
    }
    previewWindow.opener = null;
    try {
      const blob = await documentosApi.downloadDocumento(documento.id);
      const objectUrl = URL.createObjectURL(blob);
      previewWindow.location.href = objectUrl;
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err: any) {
      previewWindow.close();
      setErrorDocumentos('Error previsualizando documento: ' + (err.message || 'Error desconocido'));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Función para enviar a minutas — prefill con datos de la última minuta
  const abrirMinutaDialog = async () => {
    let juzgado = '';
    let ciudad = '';
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/minutas`);
      const ultimaConDatos = (data as any[])
        .filter(m => m.juzgado && m.ciudad)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      if (ultimaConDatos) {
        juzgado = ultimaConDatos.juzgado;
        ciudad = ultimaConDatos.ciudad;
      }
    } catch { /* si falla, dejamos vacíos y el usuario los teclea */ }

    setMinutaForm({
      juzgado,
      ciudad,
      fecha_presentacion_minuta: new Date().toISOString().split('T')[0],
      notas: '',
    });
    setMinutaDialog(true);
  };

  const enviarAMinutas = async () => {
    if (!expediente) return;
    setEnviandoMinuta(true);
    try {
      const { data: minuta } = await axios.post(`${API_BASE_URL}/api/minutas`, {
        expediente_id: expediente.id,
        juzgado: minutaForm.juzgado || null,
        ciudad: minutaForm.ciudad || null,
        num_procedimiento: expediente.codigo_actual ?? null,
        importe_minuta: expediente.importe ?? null,
        fecha_presentacion_minuta: minutaForm.fecha_presentacion_minuta || null,
        notas: minutaForm.notas || null,
      });
      setMinutaDialog(false);
      setMinutaSnack('Abriendo la minuta de la cadena procesal');
      navigate(`/minutas/${minuta.id}`);
    } catch (e: any) {
      setMinutaSnack('Error: ' + (e?.response?.data?.detail ?? 'No se pudo añadir'));
    } finally {
      setEnviandoMinuta(false);
    }
  };

  // Funciones de edición
  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = window.confirm('¿Estás seguro? Se perderán los cambios no guardados.');
      if (!confirmed) return;
    }
    
    // Restaurar datos originales
    if (originalData) {
      setFormData({
        nig: originalData.nig || '',
        codigo_actual: originalData.codigo_actual || '',
        codigo_original: originalData.codigo_original || '',
        tipo: originalData.tipo || '',
        numero: originalData.numero || null,
        anio: originalData.anio || null,
        estado: originalData.estado || '',
        justicia_gratuita: originalData.justicia_gratuita || false,
        fecha_inicio: originalData.fecha_inicio || '',
        fecha_encargo: originalData.fecha_encargo || '',
        fecha_informe: originalData.fecha_informe || '',
        importe: originalData.importe || null,
        importe_iva: originalData.importe_iva || null,
        cobrado: originalData.cobrado || false,
        observaciones_generales: originalData.observaciones_generales || '',
        nivel: originalData.nivel || null,
        proviene_de: originalData.proviene_de || '',
        se_ha_convertido_en: originalData.se_ha_convertido_en || '',
        cliente_id: originalData.cliente_id || '',
        tecnico_id: originalData.tecnico_id || ''
      });
    }
    
    setEditMode(false);
    setHasChanges(false);
  };

  const handleSave = async () => {
    if (!id || !hasChanges) return;

    try {
      setSaving(true);
      
      // LIMPIAR DATOS: Convertir cadenas vacías en null
      const cleanedData = Object.fromEntries(
        Object.entries(formData).map(([key, value]: [string, any]) => [
          key,
          value === "" ? null : value
        ])
      );
      
      console.log("🚀 Datos limpiados:", JSON.stringify(cleanedData, null, 2));
      
      const updatedExpediente = await expedientesApi.updateExpediente(id, cleanedData);

      console.log("🔥 Expediente actualizado recibido:", updatedExpediente);
      setExpediente(updatedExpediente);
      setOriginalData(updatedExpediente);
      setEditMode(false);
      setHasChanges(false);
      
      console.log('Expediente actualizado correctamente');
      
    } catch (err: any) {
      setError(err.message || 'Error actualizando expediente');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNavigateBack = () => {
    if (hasChanges) {
      const confirmed = window.confirm('¿Estás seguro? Se perderán los cambios no guardados.');
      if (!confirmed) return;
    }
    navigate('/expedientes');
  };

  // Función para navegar a expediente relacionado
  const handleNavigateToRelatedExpediente = async (codigo: string) => {
    try {
      const expedientes = await expedientesApi.getExpedientes(0, 1000);
      const expedienteRelacionado = expedientes.find(exp => exp.codigo_actual === codigo);
      
      if (expedienteRelacionado) {
        navigate(`/expedientes/${expedienteRelacionado.id}`);
      } else {
        alert(`No se encontró el expediente con código: ${codigo}`);
      }
    } catch (error) {
      console.error('Error buscando expediente relacionado:', error);
      alert('Error al buscar el expediente relacionado');
    }
  };





  // Función para manejar selección de tipo documento
  const handleTipoDocumento = (tipo: string) => {
    setModalOpen(false);
    if (tipo === 'dictamen_pericial') {
      // Abrir dialog inline para dictamen (no navegar)
      setDictamenForm(f => ({
        ...f,
        fecha_documento: new Date().toISOString().slice(0, 10),
        tipo_procedimiento_completo: expediente?.tipo
          ? `${expediente.tipo.toUpperCase()} - `
          : '',
      }));
      setDictamenDialog(true);
    } else {
      navigate(`/documentos/generar?expediente=${expediente.id}&tipo=${tipo}`);
    }
  };

  // ── Helpers para el formulario de Dictamen Pericial ──────────────────────

  /** Formatea fecha ISO a texto en español */
  const fmtFechaES = (fecha: string) => {
    if (!fecha) return '____';
    const meses = ['enero','febrero','marzo','abril','mayo','junio','julio',
                   'agosto','septiembre','octubre','noviembre','diciembre'];
    const [y, m, d] = fecha.split('-');
    return `${parseInt(d)} de ${meses[parseInt(m) - 1]} de ${y}`;
  };

  // ── Generadores de texto automáticos ─────────────────────────────────────

  /** Sec 01: texto según tipo de objeto examinado */
  const genTextoObjeto = (tipo: string): string => {
    if (tipo === 'desperfectos')
      return 'Se trata de delimitar el valor de los desperfectos ocasionados, según lo actuado en el Procedimiento de referencia.';
    if (tipo === 'sustraidos')
      return 'Se trata de delimitar el valor de los objetos sustraídos, según lo actuado en el Procedimiento de referencia.';
    return '';
  };

  /** Sec 03: texto para observaciones previas según visita/demandante */
  const genTextoObsPrevias = (f: typeof dictamenForm): string => {
    const partes: string[] = [];
    if (f.visita_realizada && f.visita_fecha)
      partes.push(`Con fecha ${fmtFechaES(f.visita_fecha)} se procedió a la inspección ocular del lugar de los hechos.`);
    else if (f.visita_realizada && !f.visita_fecha)
      partes.push('Se procedió a la inspección ocular del lugar de los hechos.');

    if (f.hablo_demandante === true) {
      const fd = f.demandante_fecha ? fmtFechaES(f.demandante_fecha) : '____';
      partes.push(`Con fecha ${fd} se mantuvo entrevista con el/la denunciante.`);
    } else if (f.hablo_demandante === false) {
      partes.push('No se ha podido mantener comunicación con el/la denunciante.');
    }
    return partes.join('\n');
  };

  /** Sec 04: texto para observaciones a la valoración según opciones */
  const genTextoObsValoracion = (f: typeof dictamenForm, moVal: number): string => {
    const partes: string[] = [];
    if (f.obs_aportaron_docs)
      partes.push(
        'Los precios de los documentos aportados, se han contrastado con precios de mercado, y también, ' +
        'con los precios de las bases estadísticas que posee quién suscribe, siendo estos coherentes con ' +
        'los mismos a fecha de autos.'
      );
    if (f.obs_obras)
      partes.push(
        'Los precios recogidos en los documentos aportados han sido contrastados con los valores de mercado, ' +
        'con las bases estadísticas de las que dispone quien suscribe, así como con la base de precios de la ' +
        'Junta de Extremadura, resultando todos ellos coherentes a la fecha de autos.'
      );
    if (f.obs_no_contrastados) {
      partes.push(
        'Dado que los daños causados no han podido ser contrastados, la valoración se hace según documentación ' +
        'obrante en autos y datos recabados y contrastados con precios de mercado y talleres consultados.'
      );
      partes.push(
        'Para la valoración he considerado la aplicación, en las subsanaciones y reposiciones necesarias, de ' +
        'materiales y elementos de características tales que posibiliten el uso normal para el que racionalmente ' +
        'están destinados los elementos cuyos daños denunciados se estudian.'
      );
    } else if (f.obs_generico) {
      partes.push(
        'Para la valoración he considerado la aplicación, en las subsanaciones y reposiciones necesarias, de ' +
        'materiales y elementos de características tales que posibiliten el uso normal para el que racionalmente ' +
        'están destinados los elementos en estudio.'
      );
    }
    if (moVal === 0 && f.desglose_origen !== '')
      partes.push('Al ser productos manufacturados, no conllevan mano de obra.');
    return partes.join('\n\n');
  };

  // ── CRUD helpers ─────────────────────────────────────────────────────────

  const dictamenAddExtremo = () => {
    const val = dictamenForm.extremo_nuevo.trim();
    if (!val) return;
    setDictamenForm(f => ({
      ...f,
      extremos_propuestos: [...f.extremos_propuestos, val],
      extremo_nuevo: '',
    }));
  };

  const dictamenRemoveExtremo = (idx: number) => {
    setDictamenForm(f => ({
      ...f,
      extremos_propuestos: f.extremos_propuestos.filter((_, i) => i !== idx),
    }));
  };

  const dictamenAddPartida = () => {
    const desc    = dictamenForm.partida_nueva_desc.trim();
    const importe = dictamenForm.partida_nueva_importe.trim();
    if (!desc || !importe) return;
    setDictamenForm(f => ({
      ...f,
      partidas: [...f.partidas, { descripcion: desc, importe }],
      partida_nueva_desc: '',
      partida_nueva_importe: '',
    }));
  };

  const dictamenRemovePartida = (idx: number) => {
    setDictamenForm(f => ({
      ...f,
      partidas: f.partidas.filter((_, i) => i !== idx),
    }));
  };

  /** Total sin IVA de todas las partidas */
  const dictamenTotalSinIva = () =>
    dictamenForm.partidas.reduce((acc, p) => acc + (parseFloat(p.importe) || 0), 0);

  /** Calcula IVA y total con IVA */
  const dictamenIVA = () => {
    const base  = dictamenTotalSinIva();
    const iva   = Math.round(base * 0.21 * 100) / 100;
    const total = Math.round((base + iva) * 100) / 100;
    return { base, iva, total };
  };

  /** Calcula el desglose MO/Materiales, completando el valor faltante */
  const dictamenDesglose = () => {
    const base = dictamenTotalSinIva();
    const mo  = parseFloat(dictamenForm.mano_obra_sin_iva)  || 0;
    const mat = parseFloat(dictamenForm.materiales_sin_iva) || 0;
    if (dictamenForm.desglose_origen === 'mano_obra')
      return { mo, mat: Math.round((base - mo) * 100) / 100 };
    if (dictamenForm.desglose_origen === 'materiales')
      return { mo: Math.round((base - mat) * 100) / 100, mat };
    return { mo, mat };
  };

  /** Cuando el usuario escribe en el campo MO — recalcula Mat y actualiza obs si MO→0 */
  const onChangeMO = (val: string) => {
    const base = dictamenTotalSinIva();
    const mo   = parseFloat(val) || 0;
    const mat  = Math.round((base - mo) * 100) / 100;
    setDictamenForm(f => {
      const newF = {
        ...f,
        mano_obra_sin_iva:  val,
        materiales_sin_iva: mat >= 0 ? mat.toString() : '0',
        desglose_origen:    'mano_obra' as const,
      };
      newF.observaciones_valoracion = genTextoObsValoracion(newF, mo);
      return newF;
    });
  };

  /** Cuando el usuario escribe en el campo Materiales — recalcula MO */
  const onChangeMat = (val: string) => {
    const base = dictamenTotalSinIva();
    const mat  = parseFloat(val) || 0;
    const mo   = Math.round((base - mat) * 100) / 100;
    setDictamenForm(f => {
      const newF = {
        ...f,
        materiales_sin_iva: val,
        mano_obra_sin_iva:  mo >= 0 ? mo.toString() : '0',
        desglose_origen:    'materiales' as const,
      };
      newF.observaciones_valoracion = genTextoObsValoracion(newF, mo >= 0 ? mo : 0);
      return newF;
    });
  };

  /** Envía la petición al backend para generar el PDF */
  const handleGenerarDictamen = async () => {
    if (!expediente) return;

    if (!dictamenForm.objeto_examen_tipo) {
      setDictamenSnack('⚠️ Selecciona el tipo de objeto de examen (sección 01)');
      return;
    }
    if (!dictamenForm.descripcion_objeto_examen.trim()) {
      setDictamenSnack('⚠️ El texto de la sección 01 no puede estar vacío');
      return;
    }
    if (dictamenForm.extremos_propuestos.length === 0) {
      setDictamenSnack('⚠️ Añade al menos un extremo propuesto (sección 02)');
      return;
    }
    if (dictamenForm.hablo_demandante === null) {
      setDictamenSnack('⚠️ Indica si se habló con el/la demandante (sección 03)');
      return;
    }
    if (dictamenForm.partidas.length === 0) {
      setDictamenSnack('⚠️ Añade al menos una partida de tasación (sección 04)');
      return;
    }
    if (!dictamenForm.desglose_origen) {
      setDictamenSnack('⚠️ Introduce el importe de Mano de Obra o de Materiales para el desglose (sección 04.02)');
      return;
    }

    setGenerandoDictamen(true);
    try {
      const lineas = dictamenForm.partidas.map(p => ({
        referencia:   p.descripcion.substring(0, 30),
        descripcion:  p.descripcion,
        cantidad:     1,
        precio:       parseFloat(p.importe) || 0,
        importe:      parseFloat(p.importe) || 0,
      }));

      const desglose = dictamenDesglose();

      await expedientesApi.generarDocumento({
        expediente_id: expediente.id,
        tipo_documento: 'dictamen_pericial',
        lineas,
        fecha_documento:             dictamenForm.fecha_documento,
        nota_adicional:              dictamenForm.nota_adicional,
        tipo_procedimiento_completo: dictamenForm.tipo_procedimiento_completo,
        descripcion_objeto_examen:   dictamenForm.descripcion_objeto_examen,
        extremos_propuestos:         dictamenForm.extremos_propuestos,
        observaciones_valoracion:    dictamenForm.observaciones_valoracion,
        // total_importe_palabras omitido → el backend lo genera automáticamente
        mano_obra_sin_iva:  desglose.mo,
        materiales_sin_iva: desglose.mat,
      });

      setDictamenDialog(false);
      setDictamenSnack('✅ Dictamen Pericial generado correctamente');
      loadDocumentos();
    } catch (err: any) {
      console.error('Error generando dictamen:', err);
      setDictamenSnack(`❌ Error: ${err?.response?.data?.detail || err.message || 'Error desconocido'}`);
    } finally {
      setGenerandoDictamen(false);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado?.toLowerCase()) {
      case 'activo': return 'success';
      case 'cobrado': return 'success';
      case 'proc terminados': return 'info';
      case 'convertido': return 'default';
      case 'sol cobro a jzdo': return 'secondary';
      case 'pte de clasificar': return 'warning';
      case 'en gerencia': return 'primary';
      case 'cancelado': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (!amount) return '-';
    return `${amount.toFixed(2)}€`;
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (typeof value === 'object' && value instanceof Date) return formatDate(value.toISOString());
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Opciones para campos select
  const estadoOptions = [
    { value: 'activo', label: 'Activo' },
    { value: 'Sol cobro a JZDO', label: 'Sol cobro a JZDO' },
    { value: 'En Gerencia', label: 'En Gerencia' },
    { value: 'Pte de CLASIFICAR', label: 'Pte de CLASIFICAR' },
    { value: 'COBRADO', label: 'COBRADO' },
    { value: 'Convertido', label: 'Convertido' },
    { value: 'Entregado', label: 'Entregado' },
    { value: 'Pendiente', label: 'Pendiente' },
    { value: 'Pendiente de Juicio', label: 'Pendiente de Juicio' },
    { value: 'Proc Terminados', label: 'Proc Terminados' }
  ];

  const tipoOptions = [
    { value: '', label: 'Sin especificar' },
    { value: 'ORD', label: 'ORD - Procedimiento Ordinario' },
    { value: 'DP', label: 'DP - Diligencias Previas' },
    { value: 'DPA', label: 'DPA - Diligencias Previas Abreviadas' },
    { value: 'DUD', label: 'DUD - Diligencias Urgentes/Juicio Rápido' },
    { value: 'PA', label: 'PA - Procedimiento Abreviado' },
    { value: 'EJ', label: 'EJ - Ejecutoria' },
    { value: 'ENJ', label: 'ENJ - ENJ' },
    { value: 'ETJ', label: 'ETJ - ETJ' },
    { value: 'JVB', label: 'JVB - Juicio Verbal' },
    { value: 'LEV', label: 'LEV - Leve' },
    { value: 'RAP', label: 'RAP - Recurso de Apelación' },
    { value: 'TJ', label: 'TJ - TJ' }
  ];

  // Función para obtener el label del tipo
  const getTipoLabel = (tipoValue: string | null | undefined) => {
    if (!tipoValue) return '-';
    const option = tipoOptions.find(opt => opt.value === tipoValue);
    return option ? option.label : tipoValue;
  };

  // Datos mock para documentos
  const mockDocumentos = [
    { id: 1, nombre: 'Informe_Pericial.pdf', tipo: 'PDF', tamaño: '2.5 MB', fecha: '2025-01-15', categoria: 'Informe' },
    { id: 2, nombre: 'Planos_Vivienda.dwg', tipo: 'DWG', tamaño: '15.8 MB', fecha: '2025-01-10', categoria: 'Plano' },
    { id: 3, nombre: 'Fotografias.zip', tipo: 'ZIP', tamaño: '45.2 MB', fecha: '2025-01-12', categoria: 'Fotografía' },
    { id: 4, nombre: 'Proforma.pdf', tipo: 'PDF', tamaño: '0.8 MB', fecha: '2025-01-08', categoria: 'Facturación' }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !expediente) {
    return <Alert severity="error">{error || 'Expediente no encontrado'}</Alert>;
  }

  // Agrupar campos por categorías
  const camposIdentificacion = [
    'id', 'codigo_actual', 'codigo_original', 'nig', 'numero', 'anio', 'tipo', 'firebase_doc_id'
  ];

  const camposEstado = [
    'estado', 'es_activo', 'justicia_gratuita', 'cobrado', 'nivel'
  ];

  const camposFechas = [
    'fecha_inicio', 'fecha_encargo', 'fecha_informe', 'created_at', 'updated_at'
  ];

  const camposEconomicos = [
    'importe', 'importe_iva'
  ];

  const camposRelaciones = [
    'cliente_id', 'tecnico_id', 'creado_por',
    'proviene_de', 'se_ha_convertido_en'
  ];

  const camposObservaciones = [
    'observaciones_generales'
  ];

  const todosLosCampos = Object.keys(expediente);
  const camposConocidos = [
    ...camposIdentificacion, ...camposEstado, ...camposFechas, 
    ...camposEconomicos, ...camposRelaciones, ...camposObservaciones
  ];
  const camposOtros = todosLosCampos.filter(campo => !camposConocidos.includes(campo));

// Función actualizada para obtener iconos según tipo de documento
  const getFileIcon = (tipo: string) => {
    switch (tipo.toLowerCase()) {
      // Documentos PDF
      case 'pdf':
      case 'factura':
      case 'proforma':
      case 'presupuesto':
        return <PictureAsPdf color="error" />;
      
      // Solicitudes legales
      case 'solicitud_costas':
        return <RequestPage color="primary" />;

      case 'solicitud_videoconferencia':
        return <VideoCall color="primary" />;

      // Dictamen Pericial
      case 'dictamen_pericial':
        return <Gavel sx={{ color: '#7b1fa2' }} />;

      // Documento genérico
      case 'generico':
        return <Description color="secondary" />;
      
      // Planos técnicos
      case 'dwg':
      case 'dxf':
        return <Assignment color="primary" />;
      
      // Archivos comprimidos
      case 'zip':
      case 'rar':
        return <Archive color="warning" />;
      
      // Imágenes
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Folder color="success" />;
      
      // Documentos Word
      case 'docx':
      case 'doc':
        return <InsertDriveFile color="info" />;
      
      // Por defecto
      default:
        return <InsertDriveFile color="action" />;
    }
  };

  const documentosJudiciales = documentos.filter(doc => doc.es_documentacion_judicial);
  const documentosGenerados = documentos.filter(doc => !doc.es_documentacion_judicial);
  const documentosVisibles = documentTab === 0 ? documentosGenerados : documentosJudiciales;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleNavigateBack}
            variant="outlined"
          >
            Volver
          </Button>
          <Typography variant="h4">
            Expediente {expediente.codigo_actual || expediente.id}
          </Typography>
          <Chip
            label={expediente.estado || 'Sin estado'}
            color={getEstadoColor(expediente.estado) as any}
            size="medium"
          />
          <Chip
            label={(editMode ? formData.es_activo : expediente.es_activo) ? 'Activo' : 'Inactivo'}
            color={(editMode ? formData.es_activo : expediente.es_activo) ? 'success' : 'error'}
            size="small"
          />
          {editMode && (
            <Chip
              label={hasChanges ? "Cambios sin guardar" : "Modo edición"}
              color={hasChanges ? "warning" : "info"}
              size="small"
            />
          )}
        </Box>
        <Box display="flex" gap={1} sx={{ flexWrap: 'wrap', minWidth: 0 }}>
          {/* Botones en modo NO-edición */}
          {editMode === false && (
            <>
              <Button
                startIcon={<Edit />}
                variant="contained"
                color="primary"
                onClick={handleEdit}
              >
                Editar
              </Button>

              <Button
                startIcon={<Assignment />}
                variant="contained"
                color="primary"
                onClick={() => setModalOpen(true)}
              >
                Generar Documento
              </Button>

              <Button
                startIcon={<Gavel />}
                variant="outlined"
                color="secondary"
                onClick={abrirMinutaDialog}
              >
                Añadir a Minutas
              </Button>

              <Button
                startIcon={<Delete />}
                variant="outlined"
                color="error"
                onClick={() => {
                  setErrorEliminarExpediente(null);
                  setDeleteExpedienteConfirmOpen(true);
                }}
              >
                Eliminar
              </Button>
            </>
          )}

          {/* Botones en modo EDICIÓN */}
          {editMode === true && (
            <>
              <Button
                startIcon={<Cancel />}
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                sx={{ flexShrink: 0, visibility: 'visible !important', display: 'inline-flex !important' }}
              >
                Cancelar
              </Button>
              <Button
                startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                variant="contained"
                color="success"
                onClick={handleSave}
                disabled={!hasChanges || saving}
                title={!hasChanges ? "No hay cambios para guardar" : "Guardar cambios"}
                sx={{ flexShrink: 0, visibility: 'visible !important', display: 'inline-flex !important' }}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Información básica destacada */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, 
          gap: 3 
        }}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">NIG</Typography>
            {editMode ? (
              <TextField
                fullWidth
                value={formData.nig}
                onChange={(e) => handleInputChange('nig', e.target.value)}
                variant="standard"
              />
            ) : (
              <Typography variant="h6">{expediente.nig || '-'}</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">Tipo</Typography>
            {editMode ? (
              <FormControl fullWidth variant="standard">
                <Select
                  value={formData.tipo}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                >
                  {tipoOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="h6">{getTipoLabel(expediente.tipo)}</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">Número/Año</Typography>
            {editMode ? (
              <Box display="flex" gap={1}>
                <TextField
                  type="number"
                  value={formData.numero || ''}
                  onChange={(e) => handleInputChange('numero', parseInt(e.target.value) || null)}
                  variant="standard"
                  size="small"
                  inputProps={{ min: 0 }}
                />
                <Typography variant="h6">/</Typography>
                <TextField
                  type="number"
                  value={formData.anio || ''}
                  onChange={(e) => handleInputChange('anio', parseInt(e.target.value) || null)}
                  variant="standard"
                  size="small"
                  inputProps={{ min: 2000, max: 2050 }}
                />
              </Box>
            ) : (
              <Typography variant="h6">
                {expediente.numero || '-'}/{expediente.anio || '-'}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">Importe Total</Typography>
            {editMode ? (
              <TextField
                fullWidth
                type="number"
                value={formData.importe || ''}
                onChange={(e) => handleInputChange('importe', parseFloat(e.target.value) || null)}
                variant="standard"
                inputProps={{ step: 0.01, min: 0 }}
              />
            ) : (
              <Typography variant="h6" color="primary">
                {formatCurrency(expediente.importe)}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Pestañas */}
      <Paper>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Info />} label="Identificación" />
          <Tab icon={<Settings />} label="Estado" />
          <Tab icon={<CalendarToday />} label="Fechas" />
          <Tab icon={<AttachMoney />} label="Económico" />
          <Tab icon={<AccountTree />} label="Relaciones" />
          <Tab icon={<Folder />} label="Documentos" />
          <Tab icon={<Assignment />} label="Observaciones" />
          <Tab icon={<Archive />} label="Otros Campos" />
        </Tabs>

        {/* Pestaña Identificación */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
            gap: 3 
          }}>
            {camposIdentificacion.map((campo) => (
              <Card key={campo}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {campo.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  {['id', 'created_at', 'updated_at', 'firebase_doc_id'].includes(campo) ? (
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {formatValue(expediente[campo])}
                    </Typography>
                  ) : editMode ? (
                    <TextField
                      fullWidth
                      value={formData[campo] || ''}
                      onChange={(e) => handleInputChange(campo, e.target.value)}
                      variant="outlined"
                      size="small"
                      type={campo === 'numero' || campo === 'anio' ? 'number' : 'text'}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {formatValue(expediente[campo])}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>

        {/* Pestaña Estado */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
            gap: 3 
          }}>
            {camposEstado.map((campo) => (
              <Card key={campo}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {campo.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  {editMode ? (
                    campo === 'estado' ? (
                      <FormControl fullWidth>
                        <Select
                          value={formData.estado}
                          onChange={(e) => handleInputChange('estado', e.target.value)}
                          size="small"
                        >
                          {estadoOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : ['justicia_gratuita', 'cobrado', 'es_activo'].includes(campo) ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData[campo]}
                            onChange={(e) => handleInputChange(campo, e.target.checked)}
                          />
                        }
                        label={formData[campo] ? 'SÍ' : 'NO'}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        value={formData[campo] || ''}
                        onChange={(e) => handleInputChange(campo, e.target.value)}
                        variant="outlined"
                        size="small"
                        type={campo === 'nivel' ? 'number' : 'text'}
                      />
                    )
                  ) : (
                    campo === 'estado' ? (
                      <Chip
                        label={expediente[campo] || 'Sin estado'}
                        color={getEstadoColor(expediente[campo]) as any}
                        size="medium"
                      />
                    ) : ['justicia_gratuita', 'cobrado', 'es_activo'].includes(campo) ? (
                      <Chip
                        label={expediente[campo] ? 'SÍ' : 'NO'}
                        color={expediente[campo] ? 'success' : 'default'}
                        size="small"
                      />
                    ) : (
                      <Typography variant="body1">
                        {formatValue(expediente[campo])}
                      </Typography>
                    )
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>

        {/* Pestaña Fechas */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
            gap: 3 
          }}>
            {camposFechas.map((campo) => (
              <Card key={campo}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {campo.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  {['created_at', 'updated_at'].includes(campo) ? (
                    <Typography variant="body1">
                      {expediente[campo] ? formatDate(expediente[campo]) : '-'}
                    </Typography>
                  ) : editMode ? (
                    <TextField
                      fullWidth
                      type="date"
                      value={formData[campo] ? new Date(formData[campo]).toISOString().split('T')[0] : ''}
                      onChange={(e) => handleInputChange(campo, e.target.value ? new Date(e.target.value).toISOString() : '')}
                      variant="outlined"
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1">
                      {expediente[campo] ? formatDate(expediente[campo]) : '-'}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>

        {/* Pestaña Económico */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
            gap: 3 
          }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Importes</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">Importe Base:</Typography>
                    {editMode ? (
                      <TextField
                        type="number"
                        value={formData.importe || ''}
                        onChange={(e) => handleInputChange('importe', parseFloat(e.target.value) || null)}
                        size="small"
                        inputProps={{ step: 0.01, min: 0 }}
                      />
                    ) : (
                      <Typography variant="h6" color="primary">
                        {formatCurrency(expediente.importe)}
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">IVA:</Typography>
                    {editMode ? (
                      <TextField
                        type="number"
                        value={formData.importe_iva || ''}
                        onChange={(e) => handleInputChange('importe_iva', parseFloat(e.target.value) || null)}
                        size="small"
                        inputProps={{ step: 0.01, min: 0 }}
                      />
                    ) : (
                      <Typography variant="body1">
                        {formatCurrency(expediente.importe_iva)}
                      </Typography>
                    )}
                  </Box>
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1" fontWeight="bold">Total:</Typography>
                    <Typography variant="h5" color="primary">
                      {formatCurrency((expediente.importe || 0) + (expediente.importe_iva || 0))}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Estado de Cobro</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">Cobrado:</Typography>
                    {editMode ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData.cobrado}
                            onChange={(e) => handleInputChange('cobrado', e.target.checked)}
                          />
                        }
                        label={formData.cobrado ? 'SÍ' : 'NO'}
                      />
                    ) : (
                      <Chip
                        label={expediente.cobrado ? 'SÍ' : 'NO'}
                        color={expediente.cobrado ? 'success' : 'warning'}
                        size="small"
                      />
                    )}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">Justicia Gratuita:</Typography>
                    {editMode ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData.justicia_gratuita}
                            onChange={(e) => handleInputChange('justicia_gratuita', e.target.checked)}
                          />
                        }
                        label={formData.justicia_gratuita ? 'SÍ' : 'NO'}
                      />
                    ) : (
                      <Chip
                        label={expediente.justicia_gratuita ? 'SÍ' : 'NO'}
                        color={expediente.justicia_gratuita ? 'info' : 'default'}
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        {/* PESTAÑA RELACIONES - MODIFICADA CON BÚSQUEDA Y FORMATO MEJORADO */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
            gap: 3 
          }}>
            {camposRelaciones.map((campo) => (
              <Card key={campo}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {campo.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  
                  {/* Campo cliente_id - LÓGICA ESPECIAL */}
                  {campo === 'cliente_id' ? (
                    <>
                      {editMode ? (
                        <>
                          {loadingClientes && (
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <CircularProgress size={16} />
                              <Typography variant="body2" color="textSecondary">
                                Cargando clientes...
                              </Typography>
                            </Box>
                          )}
                          <FormControl fullWidth size="small">
                            <InputLabel>Cliente</InputLabel>
                            <Select
                              value={formData.cliente_id}
                              label="Cliente"
                              onChange={(e) => handleInputChange('cliente_id', e.target.value)}
                              disabled={loadingClientes}
                            >
                              <MenuItem value="">
                                <em>Sin cliente</em>
                              </MenuItem>
                              {clientes.map(cliente => (
                                <MenuItem key={cliente.id} value={cliente.id}>
                                  <Box>
                                    <Typography variant="body1">{cliente.nombre}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                      {cliente.tipo} {cliente.codigo && `- ${cliente.codigo}`}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                          {expediente.cliente_id ? getClienteNombre(expediente.cliente_id) : '-'}
                        </Typography>
                      )}
                    </>
                  ) 
                  
                  /* Campos de relaciones con botones de búsqueda - VERSIÓN MEJORADA */
                  : (campo === 'proviene_de' || campo === 'se_ha_convertido_en') ? (
                    <>
                      <Box display="flex" gap={1} alignItems="flex-end">
                        {editMode ? (
                          <>
                            <TextField
                              fullWidth
                              value={formData[campo] || ''}
                              onChange={(e) => handleInputChange(campo, e.target.value)}
                              variant="outlined"
                              size="small"
                              placeholder="Código del expediente"
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Search />}
                              onClick={() => handleOpenSearchModal(campo)}
                              sx={{ minWidth: 'auto', px: 1 }}
                            >
                              🔍
                            </Button>
                          </>
                        ) : (
                          <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {expediente[campo] ? (
                              <FormatCodigoWithInfo codigo={expediente[campo]} />
                            ) : '-'}
                          </Typography>
                        )}
                      </Box>

                      {/* Botón para navegar a expedientes relacionados */}
                      {expediente[campo] && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1 }}
                          startIcon={<Visibility />}
                          onClick={() => handleNavigateToRelatedExpediente(expediente[campo])}
                        >
                          Ver Expediente
                        </Button>
                      )}

                      {/* Crear un expediente nuevo y vincularlo como "se ha convertido en" */}
                      {editMode && campo === 'se_ha_convertido_en' && !expediente[campo] && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ mt: 1 }}
                          startIcon={<Add />}
                          onClick={handleOpenCrearConvertidoModal}
                        >
                          Crear Nuevo Expediente
                        </Button>
                      )}
                    </>
                  ) 
                  
                  /* Campos de solo lectura del sistema */
                  : ['creado_por'].includes(campo) ? (
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                      {formatValue(expediente[campo])}
                    </Typography>
                  ) 
                  
                  /* Campo tecnico_id - SELECTOR DE TÉCNICOS */
                  : campo === 'tecnico_id' ? (
                    <>
                      {editMode ? (
                        <>
                          {loadingTecnicos && (
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <CircularProgress size={16} />
                              <Typography variant="body2" color="textSecondary">
                                Cargando técnicos...
                              </Typography>
                            </Box>
                          )}
                          <FormControl fullWidth size="small">
                            <InputLabel>Técnico</InputLabel>
                            <Select
                              value={formData.tecnico_id || ''}
                              label="Técnico"
                              onChange={(e) => handleInputChange('tecnico_id', e.target.value || null)}
                              disabled={loadingTecnicos}
                            >
                              <MenuItem value="">
                                <em>Sin técnico asignado</em>
                              </MenuItem>
                              {tecnicos.map(tecnico => (
                                <MenuItem key={tecnico.id} value={tecnico.id}>
                                  <Box>
                                    <Typography variant="body1">
                                      {tecnico.nombre} {tecnico.apellidos || ''}
                                    </Typography>
                                    {tecnico.especialidad && (
                                      <Typography variant="body2" color="textSecondary">
                                        {tecnico.especialidad}
                                      </Typography>
                                    )}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                          {expediente.tecnico_id ? getTecnicoNombre(expediente.tecnico_id) : '-'}
                        </Typography>
                      )}
                    </>
                  )
                  
                  /* Campos editables normales */
                  : editMode ? (
                    <TextField
                      fullWidth
                      value={formData[campo] || ''}
                      onChange={(e) => handleInputChange(campo, e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                      {formatValue(expediente[campo])}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>

        {/* Pestaña Documentos - VERSIÓN REAL */}
        <TabPanel value={tabValue} index={5}>
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                📁 Documentos del Expediente 
                {!loadingDocumentos && ` (${documentos.length})`}
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<Assignment />}
                  color="primary"
                  onClick={() => setModalOpen(true)}
                >
                  Generar Documento
                </Button>
                {/* Comentado hasta implementar upload */}
                {/* <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  color="secondary"
                  disabled
                >
                  Subir Archivo
                </Button> */}
              </Box>
            </Box>

            <Tabs value={documentTab} onChange={(_, value) => setDocumentTab(value)} sx={{ mb: 2 }}>
              <Tab label={`Generados (${documentosGenerados.length})`} />
              <Tab label={`Judiciales / contestaciones (${documentosJudiciales.length})`} />
            </Tabs>

            {/* Estados de carga y error */}
            {loadingDocumentos && (
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">
                  Cargando documentos...
                </Typography>
              </Box>
            )}

            {errorDocumentos && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorDocumentos(null)}>
                {errorDocumentos}
              </Alert>
            )}

            {/* Tabla de documentos */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Documento</TableCell>
                    <TableCell>Expediente de origen</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Tamaño</TableCell>
                    <TableCell>Fecha Generación</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Documentos reales de la base de datos */}
                  {documentosVisibles.length > 0 ? (
                    documentosVisibles.map((documento) => (
                      <TableRow key={documento.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getFileIcon(documento.tipo_documento)}
                            <Box>
                              <Chip 
                                label={documento.tipo_documento.toUpperCase()} 
                                size="small" 
                                color="primary"
                              />
                              {documento.generado_automaticamente && (
                                <Chip 
                                  label="AUTO" 
                                  size="small" 
                                  color="success" 
                                  sx={{ ml: 0.5 }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {documento.nombre_archivo || `${documento.tipo_documento}_${documento.id.slice(0, 8)}`}
                            </Typography>
                            {documento.numero_documento && (
                              <Typography variant="caption" color="textSecondary">
                                Nº: {documento.numero_documento}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {documento.expediente_origen_codigo || (
                              documento.expediente_origen_anio && documento.expediente_origen_numero
                                ? `${documento.expediente_origen_numero}/${documento.expediente_origen_anio}`
                                : '—'
                            )}
                          </Typography>
                          {documento.expediente_origen_tipo && (
                            <Typography variant="caption" color="textSecondary">
                              {documento.expediente_origen_tipo}
                            </Typography>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label={documento.estado || 'generado'} 
                            color={documento.estado === 'generado' ? 'success' : 'default'} 
                            size="small" 
                          />
                        </TableCell>
                        
                        <TableCell>
                          {documento.tamaño_kb ? `${documento.tamaño_kb} KB` : '-'}
                        </TableCell>
                        
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {documento.fecha_generacion 
                                ? new Date(documento.fecha_generacion).toLocaleDateString('es-ES')
                                : new Date(documento.created_at).toLocaleDateString('es-ES')
                              }
                            </Typography>
                            {documento.tiempo_generacion_segundos && (
                              <Typography variant="caption" color="textSecondary">
                                ({documento.tiempo_generacion_segundos.toFixed(1)}s)
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {/* Botón Descargar */}
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleDownloadDocumento(documento)}
                              title="Descargar documento"
                            >
                              <Download />
                            </IconButton>
                            
                            {/* Botón Ver/Preview */}
                            <IconButton 
                              size="small" 
                              color="secondary"
                              onClick={() => handlePreviewDocumento(documento)}
                              title="Ver documento"
                            >
                              <Visibility />
                            </IconButton>
                            
                            {/* Botón Eliminar */}
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleConfirmDelete(documento)}
                              disabled={deletingDocumento === documento.id}
                              title="Eliminar documento"
                            >
                              {deletingDocumento === documento.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Delete />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    /* Estado vacío */
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box py={4}>
                          <InsertDriveFile sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                          <Typography variant="body1" color="textSecondary">
                            No hay documentos generados
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Usa el botón "Generar Documento" para crear facturas, presupuestos, etc.
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<Assignment />}
                            sx={{ mt: 2 }}
                            onClick={() => setModalOpen(true)}
                          >
                            Generar Primer Documento
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Modal de confirmación para eliminar documento */}
        <Modal
          open={deleteConfirmOpen}
          onClose={() => {
            if (!deletingDocumento) {
              setDeleteConfirmOpen(false);
              setTimeout(() => {
                setDocumentoToDelete(null);
              }, 100);
            }
          }}
          aria-labelledby="modal-confirmar-eliminar"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            <Typography id="modal-confirmar-eliminar" variant="h6" component="h2" gutterBottom>
              🗑️ Confirmar Eliminación
            </Typography>
            
            {documentoToDelete && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  ¿Estás seguro de que quieres eliminar este documento?
                </Typography>
                
                <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2" fontWeight="bold">
                    📄 {documentoToDelete.nombre_archivo}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tipo: {documentoToDelete.tipo_documento} | 
                    Tamaño: {documentoToDelete.tamaño_kb || '?'} KB
                  </Typography>
                </Box>
                
                <Alert severity="warning" sx={{ mb: 3 }}>
                  Esta acción no se puede deshacer. El archivo se eliminará permanentemente.
                </Alert>
              </Box>
            )}

            {/* Botones de confirmación */}
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setTimeout(() => {
                    setDocumentoToDelete(null);
                  }, 100);
                }}
                color="secondary"
                disabled={!!deletingDocumento}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteDocumento}
                variant="contained"
                color="error"
                disabled={!!deletingDocumento}
                startIcon={deletingDocumento ? <CircularProgress size={16} /> : <Delete />}
              >
                {deletingDocumento ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Modal de confirmación para eliminar expediente */}
        <Modal
          open={deleteExpedienteConfirmOpen}
          onClose={() => {
            if (!deletingExpediente) {
              setDeleteExpedienteConfirmOpen(false);
            }
          }}
          aria-labelledby="modal-confirmar-eliminar-expediente"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            <Typography id="modal-confirmar-eliminar-expediente" variant="h6" component="h2" gutterBottom>
              🗑️ Confirmar Eliminación
            </Typography>

            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                ¿Estás seguro de que quieres eliminar este expediente?
              </Typography>

              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
                <Typography variant="body2" fontWeight="bold">
                  📁 {expediente?.codigo_actual}
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ mb: 3 }}>
                Esta acción no se puede deshacer. El expediente y sus documentos asociados se eliminarán permanentemente.
              </Alert>

              {errorEliminarExpediente && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {errorEliminarExpediente}
                </Alert>
              )}
            </Box>

            {/* Botones de confirmación */}
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                onClick={() => setDeleteExpedienteConfirmOpen(false)}
                color="secondary"
                disabled={deletingExpediente}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteExpediente}
                variant="contained"
                color="error"
                disabled={deletingExpediente}
                startIcon={deletingExpediente ? <CircularProgress size={16} /> : <Delete />}
              >
                {deletingExpediente ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Pestaña Observaciones */}
        <TabPanel value={tabValue} index={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Observaciones Generales</Typography>
              <Divider sx={{ mb: 2 }} />
              {editMode ? (
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={formData.observaciones_generales || ''}
                  onChange={(e) => handleInputChange('observaciones_generales', e.target.value)}
                  variant="outlined"
                  placeholder="Escribe las observaciones aquí..."
                />
              ) : (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', minHeight: '100px' }}>
                  {expediente.observaciones_generales || 'Sin observaciones registradas.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Pestaña Otros Campos */}
        <TabPanel value={tabValue} index={7}>
          <Typography variant="h6" gutterBottom>Campos Adicionales</Typography>
          {camposOtros.length === 0 ? (
            <Alert severity="info">No hay campos adicionales para mostrar.</Alert>
          ) : (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
              gap: 2 
            }}>
              {camposOtros.map((campo) => (
                <Card variant="outlined" key={campo}>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {campo.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {formatValue(expediente[campo])}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </TabPanel>
      </Paper>
      
      {/* Modal de búsqueda de expedientes */}
      <Modal
        open={searchModalOpen}
        onClose={handleCloseSearchModal}
        aria-labelledby="modal-buscar-expediente"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 600, md: 800 },
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          <Typography id="modal-buscar-expediente" variant="h6" component="h2" gutterBottom>
            🔍 Buscar Expediente
          </Typography>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Introduce el número exacto del expediente que buscas:
          </Typography>

          {/* Campo de búsqueda */}
          <Box display="flex" gap={2} mb={3}>
            <TextField
              fullWidth
              label="Número de Expediente"
              type="number"
              value={searchNumber}
              onChange={(e) => setSearchNumber(e.target.value)}
              placeholder="Ej: 123"
              inputProps={{ min: 1 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchExpedientes();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearchExpedientes}
              disabled={searchLoading || !searchNumber.trim()}
              startIcon={searchLoading ? <CircularProgress size={16} /> : <Search />}
              sx={{ minWidth: 120 }}
            >
              {searchLoading ? 'Buscando...' : 'Buscar'}
            </Button>
          </Box>

          {/* Error de búsqueda */}
          {searchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {searchError}
            </Alert>
          )}

          {/* Resultados de búsqueda */}
          {searchResults.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                📋 Resultados encontrados ({searchResults.length}):
              </Typography>
              
              <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Código</strong></TableCell>
                      <TableCell><strong>Tipo</strong></TableCell>
                      <TableCell><strong>Año</strong></TableCell>
                      <TableCell><strong>Cliente</strong></TableCell>
                      <TableCell><strong>Estado</strong></TableCell>
                      <TableCell><strong>Acción</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((exp) => (
                      <TableRow key={exp.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
                            {exp.codigo_actual || exp.id.slice(0, 8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={exp.tipo || 'Sin tipo'} size="small" color="primary" />
                        </TableCell>
                        <TableCell>{exp.anio || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getClienteNombre(exp.cliente_id)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={exp.estado || 'Sin estado'} 
                            size="small" 
                            color={getEstadoColor(exp.estado) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CheckCircle />}
                            onClick={() => handleSelectExpediente(exp)}
                            color="success"
                          >
                            Elegir
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Botones del modal */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={handleCloseSearchModal} color="secondary">
              Cancelar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de confirmación de cambios */}
      <Modal
        open={confirmModalOpen}
        onClose={handleCancelChanges}
        aria-labelledby="modal-confirmar-cambios"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500, md: 600 },
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          <Typography id="modal-confirmar-cambios" variant="h6" component="h2" gutterBottom>
            ⚠️ Confirmar Cambios
          </Typography>
          
          <Typography variant="body2" component="pre" sx={{ 
            whiteSpace: 'pre-wrap', 
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            backgroundColor: '#f5f5f5',
            p: 2,
            borderRadius: 1,
            mb: 3
          }}>
            {pendingChanges}
          </Typography>

          {/* Botones de confirmación */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button 
              onClick={handleCancelChanges} 
              color="secondary"
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmChanges}
              variant="contained"
              color="warning"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <Warning />}
            >
              {saving ? 'Procesando...' : 'Confirmar Cambios'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal para crear un expediente nuevo y vincularlo como "se ha convertido en" */}
      <Dialog
        open={crearConvertidoModalOpen}
        onClose={handleCloseCrearConvertidoModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Crear expediente en el que se ha convertido</Typography>
            <IconButton onClick={handleCloseCrearConvertidoModal} disabled={creandoConvertido}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Se creará un expediente nuevo y se vinculará automáticamente como sucesor de{' '}
            <strong>{expediente?.codigo_actual}</strong>. Este expediente quedará marcado como "Convertido".
          </Typography>

          {errorCrearConvertido && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorCrearConvertido}
            </Alert>
          )}

          {loadingCodigoConvertido && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="textSecondary">
                Generando código automático...
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, pt: 2 }}>
            <TextField
              fullWidth
              label="Código Actual"
              value={crearConvertidoForm.codigo_actual}
              disabled
              helperText="Generado automáticamente por el sistema"
            />

            <FormControl fullWidth required>
              <InputLabel>Tipo *</InputLabel>
              <Select
                value={crearConvertidoForm.tipo}
                label="Tipo *"
                onChange={(e) => handleCrearConvertidoInputChange('tipo', e.target.value)}
              >
                {tipoOptions.filter(opt => opt.value !== '').map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Número *"
              type="number"
              value={crearConvertidoForm.numero ?? ''}
              onChange={(e) => handleCrearConvertidoInputChange('numero', parseInt(e.target.value) || undefined)}
              required
              inputProps={{ min: 1 }}
              helperText="Número del procedimiento"
            />

            <TextField
              fullWidth
              label="Año *"
              type="number"
              value={crearConvertidoForm.anio}
              onChange={(e) => handleCrearConvertidoInputChange('anio', parseInt(e.target.value) || new Date().getFullYear())}
              required
              inputProps={{ min: 2000, max: 2050 }}
              helperText="Año del procedimiento"
            />

            <FormControl fullWidth>
              <InputLabel>Cliente</InputLabel>
              <Select
                value={crearConvertidoForm.cliente_id}
                label="Cliente"
                onChange={(e) => handleCrearConvertidoInputChange('cliente_id', e.target.value)}
              >
                <MenuItem value="">
                  <em>Sin cliente</em>
                </MenuItem>
                {clientes.map(cliente => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    <Box>
                      <Typography variant="body1">{cliente.nombre}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cliente.tipo} {cliente.codigo && `- ${cliente.codigo}`}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="NIG"
              value={crearConvertidoForm.nig}
              onChange={(e) => handleCrearConvertidoInputChange('nig', e.target.value)}
              helperText="Copiado del expediente de origen; puedes modificarlo"
            />

            <FormControl fullWidth sx={{ gridColumn: { md: 'span 2' } }}>
              <InputLabel>Técnico Asignado</InputLabel>
              <Select
                value={crearConvertidoForm.tecnico_id}
                label="Técnico Asignado"
                onChange={(e) => handleCrearConvertidoInputChange('tecnico_id', e.target.value)}
              >
                <MenuItem value="">
                  <em>Sin asignar</em>
                </MenuItem>
                {tecnicos.map(tecnico => (
                  <MenuItem key={tecnico.id} value={tecnico.id}>
                    <Box>
                      <Typography variant="body1">
                        {tecnico.nombre} {tecnico.apellidos || ''}
                      </Typography>
                      {tecnico.especialidad && (
                        <Typography variant="body2" color="textSecondary">
                          {tecnico.especialidad}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Observaciones"
              multiline
              rows={3}
              value={crearConvertidoForm.observaciones_generales}
              onChange={(e) => handleCrearConvertidoInputChange('observaciones_generales', e.target.value)}
              sx={{ gridColumn: { md: 'span 2' } }}
            />

            <Box sx={{ gridColumn: { md: 'span 2' } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={crearConvertidoForm.justicia_gratuita}
                    onChange={(e) => handleCrearConvertidoInputChange('justicia_gratuita', e.target.checked)}
                  />
                }
                label="Justicia Gratuita"
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseCrearConvertidoModal} disabled={creandoConvertido}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarCrearConvertido}
            variant="contained"
            disabled={!isCrearConvertidoFormValid() || creandoConvertido}
            startIcon={creandoConvertido ? <CircularProgress size={16} /> : <Add />}
          >
            {creandoConvertido ? 'Creando y vinculando...' : 'Crear y Vincular'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modales para generación de documentos */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-tipo-documento"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
          borderRadius: 2
        }}>
          <Typography id="modal-tipo-documento" variant="h6" component="h2" gutterBottom>
            🗂️ Seleccionar Tipo de Documento
          </Typography>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            ¿Qué tipo de documento deseas generar para este expediente?
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<Receipt />}
              onClick={() => handleTipoDocumento('factura')}
              sx={{ justifyContent: 'flex-start' }}
            >
              🧾 Factura
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<MoneyOff />}
              onClick={() => handleTipoDocumento('proforma')}
              sx={{ justifyContent: 'flex-start' }}
            >
              📄 Factura Proforma
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<Assessment />}
              onClick={() => handleTipoDocumento('presupuesto')}
              sx={{ justifyContent: 'flex-start' }}
            >
              📊 Presupuesto
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<RequestPage />}
              onClick={() => handleTipoDocumento('solicitud_costas')}
              sx={{ justifyContent: 'flex-start' }}
            >
              📋 Solicitud de Costas
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<VideoCall />}
              onClick={() => handleTipoDocumento('solicitud_videoconferencia')}
              sx={{ justifyContent: 'flex-start' }}
            >
              🎥 Solicitud Declaración por Videoconferencia
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<Description />}
              onClick={() => handleTipoDocumento('generico')}
              sx={{ justifyContent: 'flex-start' }}
            >
              📝 Documento Genérico
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<Gavel />}
              onClick={() => handleTipoDocumento('dictamen_pericial')}
              sx={{ justifyContent: 'flex-start', borderColor: '#7b1fa2', color: '#7b1fa2',
                    '&:hover': { borderColor: '#4a148c', backgroundColor: '#f3e5f5' } }}
            >
              ⚖️ Dictamen Pericial
            </Button>

          </Box>

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button onClick={() => setModalOpen(false)} color="secondary">
              Cancelar
            </Button>
          </Box>
        </Box>
      </Modal>


      {/* Dialog: Añadir a Minutas */}
      <Dialog open={minutaDialog} onClose={() => setMinutaDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Añadir al Control de Minutas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se creará una entrada en el control de cobro para el abogado.
            El importe (<strong>{expediente?.importe ? `${expediente.importe} €` : '—'}</strong>) y
            el nº de procedimiento (<strong>{expediente?.codigo_actual ?? '—'}</strong>) se copian automáticamente.
          </Typography>
          <TextField
            label="Juzgado"
            fullWidth
            value={minutaForm.juzgado}
            onChange={e => setMinutaForm(f => ({ ...f, juzgado: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Ciudad"
            fullWidth
            value={minutaForm.ciudad}
            onChange={e => setMinutaForm(f => ({ ...f, ciudad: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Fecha presentación minuta"
            type="date"
            fullWidth
            value={minutaForm.fecha_presentacion_minuta}
            onChange={e => setMinutaForm(f => ({ ...f, fecha_presentacion_minuta: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Notas iniciales (opcional)"
            multiline
            rows={3}
            fullWidth
            value={minutaForm.notas}
            onChange={e => setMinutaForm(f => ({ ...f, notas: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMinutaDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={enviarAMinutas}
            disabled={enviandoMinuta}
            startIcon={enviandoMinuta ? <CircularProgress size={16} /> : <Gavel />}
          >
            Añadir a Minutas
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar confirmación minutas */}
      <Snackbar
        open={Boolean(minutaSnack)}
        autoHideDuration={4000}
        onClose={() => setMinutaSnack('')}
        message={minutaSnack}
      />

      {/* ═══════════════════════════════════════════════════════
          Dialog: Generar Dictamen Pericial
          ═══════════════════════════════════════════════════════ */}
      <Dialog
        open={dictamenDialog}
        onClose={() => !generandoDictamen && setDictamenDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ bgcolor: '#7b1fa2', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Gavel /> Generar Dictamen Pericial
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>

          {/* ── CABECERA AUTO-RELLENADA ── */}
          <Alert severity="info" sx={{ mb: 3 }}>
            Los datos marcados en gris se rellenan automáticamente. Los marcados con * son obligatorios.
          </Alert>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
            <TextField
              label="Procedimiento"
              value={
                expediente
                  ? `${(expediente.tipo || '').toUpperCase()} ${expediente.numero || '?'} / ${String(expediente.anio || '').slice(-2)}`
                  : ''
              }
              InputProps={{ readOnly: true }}
              size="small"
              helperText="Auto-rellenado"
              sx={{ bgcolor: '#f9f9f9' }}
            />
            <TextField label="NIG" value={expediente?.nig ?? '(sin NIG)'} InputProps={{ readOnly: true }}
              size="small" helperText="Auto-rellenado" sx={{ bgcolor: '#f9f9f9' }} />
            <TextField label="Juzgado / Destinatario"
              value={expediente?.cliente_id ? getClienteNombre(expediente.cliente_id) : '(sin cliente)'}
              InputProps={{ readOnly: true }} size="small" helperText="Auto-rellenado" sx={{ bgcolor: '#f9f9f9' }} />
            <TextField label="Fecha del dictamen *" type="date" size="small"
              value={dictamenForm.fecha_documento}
              onChange={e => setDictamenForm(f => ({ ...f, fecha_documento: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Box>

          <TextField label="Descripción tipo de procedimiento *" fullWidth size="small" sx={{ mb: 3 }}
            placeholder="Ej: DPA - Diligencias Previas Proc. Abreviado"
            value={dictamenForm.tipo_procedimiento_completo}
            onChange={e => setDictamenForm(f => ({ ...f, tipo_procedimiento_completo: e.target.value }))}
            helperText={
              dictamenForm.tipo_procedimiento_completo && expediente
                ? `Vista previa: "${dictamenForm.tipo_procedimiento_completo} ${expediente.numero} / ${expediente.anio}"`
                : 'Se mostrará junto al código (ej: "DPA - Diligencias Previas Proc. Abreviado") en la cabecera'
            }
          />

          {/* ══════════════════════════════════════════════════════
              SECCIÓN 01 — Objeto de examen
              ══════════════════════════════════════════════════════ */}
          <Box sx={{ bgcolor: '#faf4ff', border: '1px solid #ce93d8', borderRadius: 2, p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="#7b1fa2" sx={{ mb: 1.5 }}>
              01.— Elementos objeto de examen y estudio *
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 1.5 }}>
              <RadioGroup
                value={dictamenForm.objeto_examen_tipo}
                onChange={e => {
                  const tipo = e.target.value as typeof dictamenForm.objeto_examen_tipo;
                  const texto = genTextoObjeto(tipo);
                  setDictamenForm(f => ({
                    ...f,
                    objeto_examen_tipo: tipo,
                    descripcion_objeto_examen: tipo !== 'personalizado' ? texto : f.descripcion_objeto_examen,
                  }));
                }}
              >
                <FormControlLabel value="desperfectos" control={<Radio size="small" />}
                  label="🏚 Desperfectos" />
                <FormControlLabel value="sustraidos" control={<Radio size="small" />}
                  label="🔍 Objetos sustraídos" />
                <FormControlLabel value="personalizado" control={<Radio size="small" />}
                  label="✏️ Texto personalizado" />
              </RadioGroup>
            </FormControl>

            {dictamenForm.objeto_examen_tipo && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Texto generado — puedes editarlo:
                </Typography>
                <TextField fullWidth multiline rows={2} size="small"
                  value={dictamenForm.descripcion_objeto_examen}
                  onChange={e => setDictamenForm(f => ({ ...f, descripcion_objeto_examen: e.target.value }))}
                  placeholder={dictamenForm.objeto_examen_tipo === 'personalizado'
                    ? 'Escribe la descripción del objeto de examen...'
                    : ''
                  }
                />
              </>
            )}
          </Box>

          {/* ══════════════════════════════════════════════════════
              SECCIÓN 02 — Extremos propuestos
              ══════════════════════════════════════════════════════ */}
          <Box sx={{ bgcolor: '#faf4ff', border: '1px solid #ce93d8', borderRadius: 2, p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="#7b1fa2" sx={{ mb: 1 }}>
              02.— Extremos propuestos / Conceptos a valorar *
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField size="small" fullWidth placeholder="Añadir extremo propuesto..."
                value={dictamenForm.extremo_nuevo}
                onChange={e => setDictamenForm(f => ({ ...f, extremo_nuevo: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); dictamenAddExtremo(); } }}
              />
              <Button variant="outlined" size="small" onClick={dictamenAddExtremo} sx={{ whiteSpace: 'nowrap' }}>
                + Añadir
              </Button>
            </Box>
            {dictamenForm.extremos_propuestos.length === 0
              ? <Typography variant="caption" color="text.secondary">Sin extremos aún. Añade al menos uno.</Typography>
              : (
                <List dense disablePadding>
                  {dictamenForm.extremos_propuestos.map((ext, idx) => (
                    <ListItem key={idx} sx={{ bgcolor: '#fff', border: '1px solid #e1bee7', borderRadius: 1, mb: 0.5 }}
                      secondaryAction={
                        <IconButton edge="end" size="small" onClick={() => dictamenRemoveExtremo(idx)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      }>
                      <ListItemText primary={`${idx + 1}. ${ext}`} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    </ListItem>
                  ))}
                </List>
              )
            }
          </Box>

          {/* ══════════════════════════════════════════════════════
              SECCIÓN 03 — Observaciones previas (guiada)
              ══════════════════════════════════════════════════════ */}
          <Box sx={{ bgcolor: '#faf4ff', border: '1px solid #ce93d8', borderRadius: 2, p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="#7b1fa2" sx={{ mb: 1.5 }}>
              03.— Observaciones previas a la valoración
            </Typography>

            {/* ¿Visita? */}
            <Box sx={{ mb: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={dictamenForm.visita_realizada}
                    onChange={e => {
                      const checked = e.target.checked;
                      setDictamenForm(f => {
                        const nf = { ...f, visita_realizada: checked, visita_fecha: checked ? f.visita_fecha : '' };
                        nf.nota_adicional = genTextoObsPrevias(nf);
                        return nf;
                      });
                    }} />
                }
                label="¿Se realizó visita/inspección ocular?"
              />
              {dictamenForm.visita_realizada && (
                <Box sx={{ pl: 4, mt: 0.5 }}>
                  <TextField type="date" size="small" label="Fecha de visita"
                    InputLabelProps={{ shrink: true }}
                    value={dictamenForm.visita_fecha}
                    onChange={e => setDictamenForm(f => {
                      const nf = { ...f, visita_fecha: e.target.value };
                      nf.nota_adicional = genTextoObsPrevias(nf);
                      return nf;
                    })} />
                </Box>
              )}
            </Box>

            {/* ¿Habló con demandante? */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                ¿Se habló con el/la denunciante? *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button size="small"
                  variant={dictamenForm.hablo_demandante === true ? 'contained' : 'outlined'}
                  sx={dictamenForm.hablo_demandante === true
                    ? { bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#4a148c' } }
                    : { borderColor: '#7b1fa2', color: '#7b1fa2' }}
                  onClick={() => setDictamenForm(f => {
                    const nf = { ...f, hablo_demandante: true };
                    nf.nota_adicional = genTextoObsPrevias(nf);
                    return nf;
                  })}>
                  ✅ Sí
                </Button>
                <Button size="small"
                  variant={dictamenForm.hablo_demandante === false ? 'contained' : 'outlined'}
                  color="secondary"
                  onClick={() => setDictamenForm(f => {
                    const nf = { ...f, hablo_demandante: false, demandante_fecha: '' };
                    nf.nota_adicional = genTextoObsPrevias(nf);
                    return nf;
                  })}>
                  ❌ No
                </Button>
                {dictamenForm.hablo_demandante === null && (
                  <Typography variant="caption" color="error">← respuesta obligatoria</Typography>
                )}
              </Box>
              {dictamenForm.hablo_demandante === true && (
                <Box sx={{ pl: 0.5, mt: 1 }}>
                  <TextField type="date" size="small" label="Fecha de la entrevista"
                    InputLabelProps={{ shrink: true }}
                    value={dictamenForm.demandante_fecha}
                    onChange={e => setDictamenForm(f => {
                      const nf = { ...f, demandante_fecha: e.target.value };
                      nf.nota_adicional = genTextoObsPrevias(nf);
                      return nf;
                    })} />
                </Box>
              )}
            </Box>

            {/* Texto generado */}
            {(dictamenForm.visita_realizada || dictamenForm.hablo_demandante !== null) && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Texto generado para la sección 03 — puedes editarlo o añadir más:
                </Typography>
                <TextField fullWidth multiline rows={3} size="small"
                  value={dictamenForm.nota_adicional}
                  onChange={e => setDictamenForm(f => ({ ...f, nota_adicional: e.target.value }))}
                  placeholder="(se añadirá al texto estándar de la sección)"
                />
              </>
            )}
          </Box>

          {/* ══════════════════════════════════════════════════════
              SECCIÓN 04 — Tasación
              ══════════════════════════════════════════════════════ */}
          <Box sx={{ bgcolor: '#faf4ff', border: '1px solid #ce93d8', borderRadius: 2, p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="#7b1fa2" sx={{ mb: 1 }}>
            04.01.— Partidas de tasación (importes SIN IVA) *
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              sx={{ flex: 3 }}
              placeholder="Descripción del concepto valorado..."
              value={dictamenForm.partida_nueva_desc}
              onChange={e => setDictamenForm(f => ({ ...f, partida_nueva_desc: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); dictamenAddPartida(); } }}
            />
            <TextField
              size="small"
              sx={{ flex: 1 }}
              placeholder="€ s/IVA"
              type="number"
              inputProps={{ step: '0.01', min: '0' }}
              value={dictamenForm.partida_nueva_importe}
              onChange={e => setDictamenForm(f => ({ ...f, partida_nueva_importe: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); dictamenAddPartida(); } }}
            />
            <Button variant="outlined" size="small" onClick={dictamenAddPartida}
              sx={{ whiteSpace: 'nowrap', mt: 0.5 }}>
              + Añadir
            </Button>
          </Box>
          {dictamenForm.partidas.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Sin partidas aún. Añade al menos una.
            </Typography>
          )}
          {dictamenForm.partidas.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#ede7f6' }}>
                    <TableCell><strong>Concepto</strong></TableCell>
                    <TableCell align="right"><strong>Sin IVA</strong></TableCell>
                    <TableCell align="right"><strong>IVA 21%</strong></TableCell>
                    <TableCell align="right"><strong>Con IVA</strong></TableCell>
                    <TableCell align="center"><strong>🗑</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dictamenForm.partidas.map((p, idx) => {
                    const base = parseFloat(p.importe) || 0;
                    const iva  = Math.round(base * 0.21 * 100) / 100;
                    return (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{p.descripcion}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{base.toFixed(2)} €</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {iva.toFixed(2)} €
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                          {(base + iva).toFixed(2)} €
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => dictamenRemovePartida(idx)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Fila totales */}
                  {(() => {
                    const { base, iva, total } = dictamenIVA();
                    return (
                      <TableRow sx={{ bgcolor: '#ede7f6' }}>
                        <TableCell><strong>TOTAL</strong></TableCell>
                        <TableCell align="right"><strong>{base.toFixed(2)} €</strong></TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                          <strong>{iva.toFixed(2)} €</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong style={{ color: '#4a148c' }}>{total.toFixed(2)} €</strong>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Observaciones a la valoración (guiada con opciones) ── */}
          <Box sx={{ bgcolor: '#faf4ff', border: '1px solid #ce93d8', borderRadius: 2, p: 2, mb: 2, mt: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="#7b1fa2" sx={{ mb: 1 }}>
              Observaciones a la valoración
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Selecciona los supuestos que aplican (puedes elegir varios):
            </Typography>

            {([
              { key: 'obs_aportaron_docs', label: '📄 Se han aportado valoraciones / documentos' },
              { key: 'obs_obras',          label: '🏗️ Existen importes relativos a obras (con base de precios JEx)' },
              { key: 'obs_no_contrastados',label: '❓ Los daños NO han podido ser contrastados' },
              { key: 'obs_generico',       label: '📋 Texto genérico de materiales y reposición' },
            ] as const).map(({ key, label }) => (
              <FormControlLabel key={key}
                sx={{ display: 'block', mb: 0.25 }}
                control={
                  <Checkbox size="small"
                    checked={dictamenForm[key]}
                    onChange={e => {
                      const checked = e.target.checked;
                      setDictamenForm(f => {
                        const nf = { ...f, [key]: checked };
                        const mo = parseFloat(nf.mano_obra_sin_iva) || 0;
                        nf.observaciones_valoracion = genTextoObsValoracion(nf, mo);
                        return nf;
                      });
                    }}
                  />
                }
                label={<Typography variant="body2">{label}</Typography>}
              />
            ))}

            {(dictamenForm.obs_aportaron_docs || dictamenForm.obs_obras
              || dictamenForm.obs_no_contrastados || dictamenForm.obs_generico) && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Texto generado — puedes editarlo o añadir más:
                </Typography>
                <TextField fullWidth multiline rows={4} size="small"
                  value={dictamenForm.observaciones_valoracion}
                  onChange={e => setDictamenForm(f => ({ ...f, observaciones_valoracion: e.target.value }))}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  ℹ️ Si Mano de Obra = 0 se añadirá automáticamente la nota de productos manufacturados.
                </Typography>
              </>
            )}
          </Box>

          {/* SECCIÓN 04.02 – DESGLOSE MANO DE OBRA / MATERIALES */}
          </Box>{/* ← cierre caja sección 04 */}

          <Box sx={{ bgcolor: '#faf4ff', border: '1px solid #ce93d8', borderRadius: 2, p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="#7b1fa2" sx={{ mb: 0.5 }}>
            04.02.— Desglose Mano de Obra / Materiales *
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            Introduce el valor de uno; el otro se calcula automáticamente por diferencia sobre el total sin IVA
            ({dictamenTotalSinIva().toFixed(2)} €).
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            {/* Mano de Obra */}
            <Box>
              <TextField
                fullWidth size="small"
                label="Mano de Obra (€ sin IVA)"
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                value={dictamenForm.mano_obra_sin_iva}
                onChange={e => onChangeMO(e.target.value)}
                helperText={
                  dictamenForm.desglose_origen === 'materiales'
                    ? '⬅ calculado automáticamente'
                    : 'Introduce este valor'
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: dictamenForm.desglose_origen === 'materiales' ? '#f3f3f3' : 'inherit',
                  },
                }}
              />
              {dictamenForm.mano_obra_sin_iva !== '' && (
                <Typography variant="caption" sx={{ color: 'text.secondary', pl: 1 }}>
                  + IVA 21%: {(Math.round((parseFloat(dictamenForm.mano_obra_sin_iva) || 0) * 0.21 * 100) / 100).toFixed(2)} € |
                  Total: {(Math.round((parseFloat(dictamenForm.mano_obra_sin_iva) || 0) * 1.21 * 100) / 100).toFixed(2)} €
                </Typography>
              )}
            </Box>

            {/* Materiales */}
            <Box>
              <TextField
                fullWidth size="small"
                label="Materiales (€ sin IVA)"
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                value={dictamenForm.materiales_sin_iva}
                onChange={e => onChangeMat(e.target.value)}
                helperText={
                  dictamenForm.desglose_origen === 'mano_obra'
                    ? '⬅ calculado automáticamente'
                    : 'Introduce este valor'
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: dictamenForm.desglose_origen === 'mano_obra' ? '#f3f3f3' : 'inherit',
                  },
                }}
              />
              {dictamenForm.materiales_sin_iva !== '' && (
                <Typography variant="caption" sx={{ color: 'text.secondary', pl: 1 }}>
                  + IVA 21%: {(Math.round((parseFloat(dictamenForm.materiales_sin_iva) || 0) * 0.21 * 100) / 100).toFixed(2)} € |
                  Total: {(Math.round((parseFloat(dictamenForm.materiales_sin_iva) || 0) * 1.21 * 100) / 100).toFixed(2)} €
                </Typography>
              )}
            </Box>
          </Box>

          </Box>{/* ← cierre caja sección 04.02 */}

          {/* SECCIÓN 05 – CONCLUSIONES (importe automático) */}
          <Box sx={{
            p: 2, bgcolor: '#f4f0ff', borderRadius: 2,
            border: '1.5px solid #9c4dcc', mb: 1,
          }}>
            <Typography variant="subtitle1" fontWeight="bold" color="#7b1fa2" sx={{ mb: 1 }}>
              05.— Conclusiones — Total (generado automáticamente)
            </Typography>
            {(() => {
              const { base, iva, total } = dictamenIVA();
              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Base sin IVA</Typography>
                    <Typography fontWeight="bold">{base.toFixed(2)} €</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">IVA 21%</Typography>
                    <Typography fontWeight="bold">{iva.toFixed(2)} €</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', bgcolor: '#ede7f6', borderRadius: 1, p: 0.5 }}>
                    <Typography variant="caption" color="#7b1fa2">TOTAL CON IVA</Typography>
                    <Typography fontWeight="bold" color="#4a148c" fontSize="1.1rem">
                      {total.toFixed(2)} €
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              ℹ️ El importe en letras se generará automáticamente en el PDF desde el total con IVA.
            </Typography>
          </Box>

        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDictamenDialog(false)}
            disabled={generandoDictamen}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerarDictamen}
            disabled={generandoDictamen}
            startIcon={generandoDictamen ? <CircularProgress size={16} color="inherit" /> : <Gavel />}
            sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#4a148c' } }}
          >
            {generandoDictamen ? 'Generando PDF…' : '⚖️ Generar Dictamen Pericial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar dictamen */}
      <Snackbar
        open={Boolean(dictamenSnack)}
        autoHideDuration={5000}
        onClose={() => setDictamenSnack('')}
        message={dictamenSnack}
      />

    </Box>
  );
};

export default ExpedienteDetail;